import { Suspense, lazy, Component, ReactNode } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { NotFound } from './NotFound'
import { ServerError } from './ServerError'
import { purchaseRoutes } from '@/modules/purchase/routes'
import { pmRoutes } from '@/modules/pm/routes'
import { setupHubRoutes } from '@/modules/setup-hub/routes'
import { authRoutes } from '@/features/auth/routes'
import { hrRoutes, hrRootRoutes } from '@/modules/hr/routes'
import { workspaceRoutes } from '@/modules/workspace/routes'
import { PageLoader } from '@/components/ui/AgentLoader'

/**
 * Error boundary specifically for lazy-loaded route components.
 * Catches chunk loading failures and displays a friendly error.
 */
class LazyLoadErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; isChunkError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, isChunkError: false }
  }

  static getDerivedStateFromError(error: Error) {
    // Check if it's a chunk loading error
    const isChunkError = error.message.includes('Loading chunk') ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.name === 'ChunkLoadError'
    return { hasError: true, isChunkError }
  }

  componentDidCatch(error: Error) {
    console.error('[LazyLoad] Failed to load component:', error)
  }

  handleRetry = () => {
    // Clear the error and try again (will re-fetch the chunk)
    this.setState({ hasError: false, isChunkError: false })
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center text-center px-4">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
            <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            {this.state.isChunkError ? 'Failed to load page' : 'Something went wrong'}
          </h2>
          <p className="mb-4 text-sm text-gray-600">
            {this.state.isChunkError
              ? 'There was a problem loading this page. This can happen after an update.'
              : 'An error occurred while loading this page.'}
          </p>
          <button
            onClick={this.handleRetry}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Reload page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

/**
 * Wrapper for lazy-loaded components with error boundary and suspense.
 */
function LazyRoute({ children }: { children: ReactNode }) {
  return (
    <LazyLoadErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </LazyLoadErrorBoundary>
  )
}

// Lazy load pages
const DashboardHome = lazy(() => import('@/pages/DashboardHome'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const HelpPage = lazy(() => import('@/pages/HelpPage'))
const SettingsLayout = lazy(() => import('@/pages/settings/SettingsLayout'))
const NotificationsSettings = lazy(() => import('@/pages/settings/NotificationsSettings'))
const SecuritySettings = lazy(() => import('@/pages/settings/SecuritySettings'))
const AppearanceSettings = lazy(() => import('@/pages/settings/AppearanceSettings'))
const LanguageSettings = lazy(() => import('@/pages/settings/LanguageSettings'))

/**
 * Central router. Each ported module contributes its own `routes` array
 * (mirrors the per-module router.js files in the Vue app). Lazy-load each
 * module so the bundle stays split per module, exactly like today.
 */
export const router = createBrowserRouter([
  // Root redirects to login
  {
    path: '/',
    element: <Navigate to="/auth/login" replace />,
  },

  // Employee chat redirect
  {
    path: '/employee-chat',
    element: <Navigate to="/dashboard/employee-chat" replace />,
  },

  // Server error page (for 502, 503, 504, 500 errors)
  {
    path: '/error',
    element: <ServerError />,
  },

  // Auth routes (outside DashboardLayout - no navigation)
  ...authRoutes.map((route) => ({
    ...route,
    element: <LazyRoute>{route.element}</LazyRoute>,
  })),

  // HR routes at /hr/* (with DashboardLayout)
  {
    path: '/hr',
    element: <DashboardLayout />,
    errorElement: <NotFound />,
    children: hrRootRoutes,
  },

  // App routes (inside DashboardLayout - after login)
  {
    path: '/dashboard',
    element: <DashboardLayout />,
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: <LazyRoute><DashboardHome /></LazyRoute>,
      },
      {
        path: 'profile',
        element: <LazyRoute><ProfilePage /></LazyRoute>,
      },
      {
        path: 'help',
        element: <LazyRoute><HelpPage /></LazyRoute>,
      },
      {
        path: 'settings',
        element: <LazyRoute><SettingsLayout /></LazyRoute>,
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard/settings/notifications" replace />,
          },
          {
            path: 'notifications',
            element: <LazyRoute><NotificationsSettings /></LazyRoute>,
          },
          {
            path: 'security',
            element: <LazyRoute><SecuritySettings /></LazyRoute>,
          },
          {
            path: 'appearance',
            element: <LazyRoute><AppearanceSettings /></LazyRoute>,
          },
          {
            path: 'language',
            element: <LazyRoute><LanguageSettings /></LazyRoute>,
          },
        ],
      },
      ...purchaseRoutes,
      ...pmRoutes,
      ...hrRoutes,
      ...setupHubRoutes,
      ...workspaceRoutes,
      { path: '*', element: <NotFound /> },
    ],
  },

  // Catch-all 404
  {
    path: '*',
    element: <NotFound />,
  },
], {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
})
